export const FAILURE_STATES = new Set(["REJECTED", "PROTECTION_FAILED", "OUTCOME_UNKNOWN", "ACTION_FAILED"]);
const HIDDEN_ORDER_STATES = new Set(["ORDER_ACCEPTED", "PARTIALLY_FILLED", "EXPIRED"]);
const LABELS = {
  PRICE_NOTIFY_STARTED: "Notify started", PRICE_NOTIFY_REACHED: "Price reached",
  PRICE_NOTIFY_CONFIRMED: "Price notification confirmed", PRICE_NOTIFY_CANCELLED: "Price notification stopped",
  PROCESS_STARTED: "Process started",
  ORDER_ACCEPTED: "Order accepted", PARTIALLY_FILLED: "Order partially filled", FILLED: "Order filled",
  CANCELLED: "Order cancelled", EXPIRED: "Order expired", REJECTED: "Order rejected",
  PROTECTION_FAILED: "Protection failed", OUTCOME_UNKNOWN: "Execution outcome unknown",
  ACTION_REQUIRED: "Trading unlock required", ACTION_RESOLVED: "Trading unlock resolved",
  ACTION_FAILED: "Trading authentication required", ACTION_CANCELLED: "Trading request cancelled", ACTION_EXPIRED: "Trading request expired",
};

export function notificationKey(event) {
  if (event.priceAlert) return JSON.stringify(["price-alert", event.accountRef, event.priceAlert.id]);
  if (event.interactionId) return JSON.stringify(["interaction", event.sessionId, event.interactionId]);
  if (event.orderId && event.accountRef && event.exchange && event.product) {
    return JSON.stringify(["order", event.accountRef, event.exchange, event.product, event.symbol, event.orderId]);
  }
  return event.eventId;
}

export function notificationLabel(event, now = Date.now()) {
  if (event.state === "PROCESS_STARTED") return `STARTED ${event.positionSide} PROCESS`;
  if (event.state === "ACTION_REQUIRED" && (event.expiresAt <= now || event.historical)) return "Trading unlock request expired";
  return LABELS[event.state] || "Trading activity";
}

export function notificationDescription(event, now = Date.now()) {
  if (event.priceAlert) {
    if (event.state === "PRICE_NOTIFY_STARTED") return `Target: ${event.price} USDT. You’ll receive a Telegram notification when price reaches or approaches this level.`;
    if (event.state === "PRICE_NOTIFY_REACHED") return `Target: ${event.price} USDT. Price has reached or approached your level. Confirm to stop the notification process and Telegram reminders.`;
    return `Target: ${event.price} USDT. The notification process has stopped.`;
  }
  if (event.state === "PROCESS_STARTED" && event.processStart) {
    const start = event.processStart;
    return [`Symbol: ${event.symbol}`, `Position size: ${start.positionSize}`,
      `Candle: ${start.candleTime}  ${start.candleColor}`, `Entry at: ${event.price}, on ${start.entryType}`,
      `Entry price protect: ${start.entryProtection}`,
      start.stopLoss ? `Stop loss at: ${start.stopLoss}${start.potentialLoss != null ? `, potential loss -${start.potentialLoss} USD` : ""}` : "Stop loss: NONE",
      ...(start.takeProfitRatios || []).map((ratio, index) => ratio != null ? `RR for TP${index + 1}: 1 to ${ratio}` : null),
    ].filter(Boolean).join("\n");
  }
  if (event.state === "ACTION_REQUIRED") return event.expiresAt > now && !event.historical
    ? "Trading is locked. Enter your Cryptember password in Telegram to continue."
    : "This request is no longer actionable. Review your activity before submitting a fresh command.";
  if (event.state === "OUTCOME_UNKNOWN") return "Check your positions and order history before submitting another command.";
  if (event.state === "PROTECTION_FAILED") return "Review the position and its protection orders.";
  if (event.state === "ACTION_FAILED") return "Complete authentication in Telegram. Your password is never entered here.";
  const fill = ["FILLED", "PARTIALLY_FILLED"].includes(event.state);
  const size = event.cumulativeQuantity || (fill ? event.quantity : null);
  return [size ? `${size} executed` : null, event.price ? `${fill ? "at" : "Order price"} ${event.price}` : null,
    event.positionSide && event.positionSide !== "BOTH" ? event.positionSide.toLowerCase() : null].filter(Boolean).join(" · ");
}

function compareDecimalStrings(left, right) {
  if (typeof left !== "string" || typeof right !== "string" || !/^\d+(?:\.\d+)?$/.test(left) || !/^\d+(?:\.\d+)?$/.test(right)) return 0;
  const [leftInteger, leftFraction = ""] = left.split(".");
  const [rightInteger, rightFraction = ""] = right.split(".");
  const a = leftInteger.replace(/^0+/, "") || "0";
  const b = rightInteger.replace(/^0+/, "") || "0";
  if (a.length !== b.length) return a.length > b.length ? 1 : -1;
  if (a !== b) return a > b ? 1 : -1;
  const width = Math.max(leftFraction.length, rightFraction.length);
  const af = leftFraction.padEnd(width, "0"); const bf = rightFraction.padEnd(width, "0");
  return af === bf ? 0 : af > bf ? 1 : -1;
}

export function groupNotifications(records) {
  const groups = new Map();
  records.forEach(record => {
    // Show completed fills and cancellations, without intermediate or expired
    // order updates in popups, history, expanded details or unread counts.
    if (HIDDEN_ORDER_STATES.has(record.event.state)) return;
    const key = notificationKey(record.event);
    const group = groups.get(key) || { key, records: [], latest: record, cursor: record.cursor };
    group.records.push(record); group.cursor = Math.max(group.cursor, record.cursor);
    const old = group.latest.event;
    const incoming = record.event;
    const oldTime = old.occurredAt || old.observedAt;
    const incomingTime = incoming.occurredAt || incoming.observedAt;
    // Late partial observations never replace an already observed terminal order state.
    const terminal = state => ["FILLED", "CANCELLED", "EXPIRED", "REJECTED", "PRICE_NOTIFY_CONFIRMED", "PRICE_NOTIFY_CANCELLED"].includes(state);
    const priceStages = { PRICE_NOTIFY_STARTED: 1, PRICE_NOTIFY_REACHED: 2, PRICE_NOTIFY_CONFIRMED: 3, PRICE_NOTIFY_CANCELLED: 3 };
    const stalePriceAlert = old.priceAlert && priceStages[old.state] > priceStages[incoming.state];
    const stalePartial = terminal(old.state) && ["ORDER_ACCEPTED", "PARTIALLY_FILLED"].includes(incoming.state);
    const staleInteraction = ["ACTION_RESOLVED", "ACTION_CANCELLED", "ACTION_EXPIRED"].includes(old.state)
      && ["ACTION_REQUIRED", "ACTION_FAILED"].includes(incoming.state);
    const resolvedInteraction = ["ACTION_RESOLVED", "ACTION_CANCELLED", "ACTION_EXPIRED"].includes(incoming.state)
      && ["ACTION_REQUIRED", "ACTION_FAILED"].includes(old.state);
    const lowerProgress = old.state === "PARTIALLY_FILLED" && (incoming.state === "ORDER_ACCEPTED"
      || (incoming.state === "PARTIALLY_FILLED" && compareDecimalStrings(incoming.cumulativeQuantity, old.cumulativeQuantity) < 0));
    const greaterProgress = incoming.state === "PARTIALLY_FILLED" && old.state === "PARTIALLY_FILLED"
      && compareDecimalStrings(incoming.cumulativeQuantity, old.cumulativeQuantity) > 0;
    if (!stalePriceAlert && !stalePartial && !staleInteraction && !lowerProgress && (resolvedInteraction || greaterProgress || incomingTime > oldTime || (incomingTime === oldTime && record.cursor > group.latest.cursor)
      || (terminal(incoming.state) && !terminal(old.state)))) group.latest = record;
    groups.set(key, group);
  });
  return [...groups.values()].sort((a, b) => b.cursor - a.cursor);
}

export function validNotificationRecord(record) {
  const event = record?.event;
  return Number.isSafeInteger(record?.cursor) && record.cursor > 0 && event?.version === 1
    && (!event.state?.startsWith("PRICE_NOTIFY_") || (event.priceAlert
      && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(event.priceAlert.id)
      && /^[A-Z0-9]{1,30}$/.test(event.priceAlert.currency) && Number(event.price) > 0))
    && typeof event.eventId === "string" && event.eventId.length <= 200 && Object.hasOwn(LABELS, event.state)
    && Number.isSafeInteger(event.observedAt) && event.observedAt > 0 && event.observedAt < 8.64e15
    && (event.occurredAt === undefined || (Number.isSafeInteger(event.occurredAt) && event.occurredAt > 0 && event.occurredAt < 8.64e15));
}
